import {ContentContainer} from '@coolcolduk/ui-mui';
import {Grid, Paper, Typography} from '@mui/material';
import PageHeader from '../../components/PageHeader';
import {PrivateRouteProps} from '../../config/routeType';
import useListUserProgress from '../../hooks/userProgress/useListUserProgress';
import useUserCompletedSessions from '../../hooks/quizSession/useUserCompletedSessions';
import AccuracyTrendChart from './components/AccuracyTrendChart';
import OverallStatsCard from './components/OverallStatsCard';
import StudyTimeChart from './components/StudyTimeChart';
import TopicBreakdownCard from './components/TopicBreakdownCard';
import TopicPerformanceChart from './components/TopicPerformanceChart';

export default function AnalyticsPage({user: _user}: PrivateRouteProps) {
  const {progressList, loading: progressLoading} = useListUserProgress();
  const {sessions, loading: sessionsLoading} = useUserCompletedSessions({limit: 50});

  const calculateOverallStats = () => {
    if (!progressList || progressList.length === 0) {
      return {
        totalQuestions: 0,
        overallAccuracy: 0,
        totalSessions: 0,
        averageScore: 0,
      };
    }

    const totalCorrect = progressList.reduce((sum, progress) => sum + progress.totalCorrect, 0);
    const totalAnswered = progressList.reduce((sum, progress) => sum + progress.totalAnswered, 0);
    const overallAccuracy = totalAnswered > 0 ? Math.round((totalCorrect / totalAnswered) * 100) : 0;
    const averageScore = progressList.reduce((sum, progress) => sum + progress.totalScore, 0) / progressList.length;

    return {
      totalQuestions: totalAnswered,
      overallAccuracy,
      totalSessions: sessions?.length || 0,
      averageScore: Math.round(averageScore * 10) / 10,
    };
  };

  const stats = calculateOverallStats();

  return (
    <ContentContainer data-testid="analytics-page">
      <PageHeader title="Your Progress" subtitle="See how you're doing and what to focus on next" />

      {/* Overall Stats Cards */}
      <Grid container spacing={3} sx={{mb: 4}}>
        <Grid size={{xs: 12, sm: 6, md: 3}}>
          <OverallStatsCard
            title="Total Questions"
            value={stats.totalQuestions}
            subtitle="Questions answered"
            color="primary"
            loading={progressLoading}
          />
        </Grid>
        <Grid size={{xs: 12, sm: 6, md: 3}}>
          <OverallStatsCard
            title="Accuracy"
            value={`${stats.overallAccuracy}%`}
            subtitle="Overall performance"
            color="success"
            loading={progressLoading}
          />
        </Grid>
        <Grid size={{xs: 12, sm: 6, md: 3}}>
          <OverallStatsCard
            title="Study Sessions"
            value={stats.totalSessions}
            subtitle="Sessions completed"
            color="info"
            loading={sessionsLoading}
          />
        </Grid>
        <Grid size={{xs: 12, sm: 6, md: 3}}>
          <OverallStatsCard
            title="Average Score"
            value={stats.averageScore}
            subtitle="Out of 5.0"
            color="warning"
            loading={progressLoading}
          />
        </Grid>
      </Grid>

      {/* Charts Row 1 */}
      <Grid container spacing={3} sx={{mb: 4}}>
        <Grid size={{xs: 12, lg: 8}}>
          <Paper sx={{p: 3}}>
            <Typography variant="h6" sx={{fontWeight: 600, mb: 3}}>
              Topic Performance
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{mb: 3}}>
              How well you&apos;re doing in each topic (higher is better)
            </Typography>
            <TopicPerformanceChart progressList={progressList} loading={progressLoading} />
          </Paper>
        </Grid>
        <Grid size={{xs: 12, lg: 4}}>
          <TopicBreakdownCard progressList={progressList} loading={progressLoading} />
        </Grid>
      </Grid>

      {/* Charts Row 2 */}
      <Grid container spacing={3} sx={{mb: 4}}>
        <Grid size={{xs: 12, lg: 6}}>
          <Paper sx={{p: 3}}>
            <Typography variant="h6" sx={{fontWeight: 600, mb: 3}}>
              Accuracy Over Time
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{mb: 3}}>
              Your accuracy is getting better over time
            </Typography>
            <AccuracyTrendChart sessions={sessions} loading={sessionsLoading} />
          </Paper>
        </Grid>
        <Grid size={{xs: 12, lg: 6}}>
          <Paper sx={{p: 3}}>
            <Typography variant="h6" sx={{fontWeight: 600, mb: 3}}>
              Study Activity
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{mb: 3}}>
              When you study most (last 30 days)
            </Typography>
            <StudyTimeChart sessions={sessions} loading={sessionsLoading} />
          </Paper>
        </Grid>
      </Grid>
    </ContentContainer>
  );
}
